import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatSidenav } from '@angular/material/sidenav';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { Appointment } from '@interfaces/appointment';
import { Client } from '@interfaces/client';
import { DWS } from '@interfaces/dws';
import { Contact } from '@interfaces/dws/contact';
import { Employee } from '@interfaces/dws/employee';
import { AccountsService, Login } from '@services/accounts.service';
import { AppointmentsService } from '@services/appointments.service';
import { ContactsService, ExternalCalendarSyncService } from '@services/dws';
import { EmployeesService } from '@services/dws/employees.service';
import { HostClientsService } from '@services/host-clients.service';
import { tr } from '@util/tr';
import { confirm } from '@web/app/components/dialogs/confirm';
import { toast } from '@web/util/toast';
import * as moment from 'moment';
import { Moment } from 'moment';
import { Observable } from 'rxjs';
import { delay, startWith, switchMap, tap, debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { LoadingComponent } from '../loading/loading.component';

marker('Fill the email or phone field.');

@Component({
  selector: 'web-appointment-detail',
  templateUrl: './appointment-detail.component.html',
  styleUrls: ['./appointment-detail.component.scss'],
})
export class AppointmentDetailComponent implements OnInit {
  form: FormGroup;
  @Output() closeClick: EventEmitter<any> = new EventEmitter<any>();
  @Input() appointment?: Appointment;
  loading: boolean = false;
  login!: Login;

  searchControl: FormControl = new FormControl();
  clientForm: FormGroup;
  clientSaveCallback: (client: Client) => void = () => {
  };
  @ViewChild('clientSideNav') clientSideNav!: MatSidenav;

  @ViewChild('trigger') trigger: any = MatAutocompleteTrigger;
  loadingClients: boolean = false;
  contacts: Contact[] = [];
  contacts$: Observable<DWS.Page<DWS.Contact>>;
  now: Date = new Date();
  contact: Contact = {};

  isSyncingInBackground: boolean = false;

  private startDateTime?: Moment;
  private endDateTime?: Moment;
  private startDateValue: Date | null = null;
  private endDateValue: Date | null = null;

  employees: Employee[] = [];

  set startDate(v: any) {
    const asDate = v ? (v instanceof Date ? v : moment(v).toDate()) : null;
    this.startDateValue = asDate && !isNaN(asDate.getTime()) ? asDate : null;
    this.startDateTime = this.startDateValue ? moment(this.startDateValue) : undefined;
    if (this._startTime) {
      this.startTime = this._startTime;
    }
    this.endDateValue = null;
    this.endDateTime = undefined;
    this._endTime = '';
    this.form.patchValue({ endTime: '' });
  }

  get startDate(): any {
    return this.startDateValue;
  }

  set endDate(v: any) {
    const asDate = v ? (v instanceof Date ? v : moment(v).toDate()) : null;
    this.endDateValue = asDate && !isNaN(asDate.getTime()) ? asDate : null;
    this.endDateTime = this.endDateValue ? moment(this.endDateValue) : undefined;
    if (this._endTime) {
      this.endTime = this._endTime;
    }
  }

  get endDate(): any {
    return this.endDateValue;
  }

  private _startTime: string = '';
  set startTime(v: string) {
    this._startTime = v;
    const [hours, minutes] = this._startTime.split(':').map(x => parseInt(x));
    this.startDateTime = moment(this.startDateTime);
    this.startDateTime.utc(true).hours(hours);
    this.startDateTime.utc(true).minutes(minutes);
    this.form.patchValue({
      startTime: this.startDateTime.toISOString()
    })
  }

  get startTime(): string {
    return this._startTime;
  }

  private _endTime: string = '';
  set endTime(v: string) {
    this._endTime = v;
    const [hours, minutes] = this._endTime.split(':').map(x => parseInt(x));
    this.endDateTime = moment(this.endDateTime);
    this.endDateTime.utc(true).hours(hours);
    this.endDateTime.utc(true).minutes(minutes);
    this.form.patchValue({
      endTime: this.endDateTime.utc(false).toISOString()
    })
  }

  get endTime(): string {
    return this._endTime;
  }

  constructor(
    private fb: FormBuilder,
    private appointmentsService: AppointmentsService,
    private cd: ChangeDetectorRef,
    private hostClients: HostClientsService,
    private accountsService: AccountsService,
    private contactsService: ContactsService,
    private externalCalendarSyncService: ExternalCalendarSyncService,
    private employeesService: EmployeesService,
  ) {
    this.form = this.fb.group({
      title: ['', Validators.required],
      place: [''],
      allDay: [false, Validators.required],
      startTime: ['', Validators.required],
      endTime: ['', Validators.required],
      firstname: [''],
      lastname: [''],
      phone: [''],
      email: [''],
      countryIso: [''],
      manualMarketing: [false],
      clientSearch: [''],
      notes: [''],
      employeeId: [''],
    })

    this.accountsService.getLogin().then((login) => {
      this.login = login!;
    })

    const startTimeControl = this.form.get('startTime')!;
    const allDayControl = this.form.get('allDay')!;

    // allDayControl.disable();

    startTimeControl.valueChanges.subscribe(_ => {
      allDayControl.enable();
    })

    this.form.get('allDay')!.valueChanges.subscribe((all_day) => {
      const endTimeControl = this.form.get('endTime')!;
      if (all_day) {
        endTimeControl.clearValidators();
        endTimeControl.patchValue('');
      } else {
        endTimeControl.setValidators([Validators.required])
      }
      endTimeControl.updateValueAndValidity();
    })

    this.clientForm = fb.group({
      firstname: ['', Validators.required],
      lastname: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phone: ['']
    })

    this.contacts$ = this
      .searchControl
      .valueChanges
      .pipe(
        startWith(''),
        debounceTime(200),
        distinctUntilChanged(),
        tap(() => {
          this.loadingClients = true;
          this.contacts = [];
        }),
        filter(() => !!this.login && !!this.login.company && !!this.login.company.id),
        switchMap(v => {
          const query = typeof v === 'string' && v.trim() !== '' ? v : undefined;
          return this.contactsService.list({
            wineryIds: [this.login.company.id],
            q: query,
            includeUnsubscribed: false,
            includeSubscribed: false
          })
        })
      )

    this.contacts$.subscribe(c => {
      this.contacts = c.content;
      this.loadingClients = false;
    });
  }

  onClientSearchFocus() {
    if (!this.login || !this.login.company || !this.login.company.id) return;
    const currentValue = typeof this.searchControl.value === 'string' ? this.searchControl.value.trim() : '';
    if (currentValue) return;

    this.contacts = [];
    this.loadingClients = true;
    this.contactsService.list({
      wineryIds: [this.login.company.id],
      q: undefined,
      includeUnsubscribed: false,
      includeSubscribed: false
    }).subscribe(c => {
      this.contacts = c.content;
      this.loadingClients = false;
      this.cd.markForCheck();
    });
  }

  ngOnInit() {
    this.employeesService.list(this.login.company.id).subscribe(employees => {
      this.employees = employees;
    });
    if (this.appointment) {
      this.form.patchValue(this.appointment);
      const start = moment(this.appointment.startTime);
      this.startDate = start.toDate();
      this.startTime = start.format('HH:mm');
      if (!this.appointment.allDay && this.appointment.endTime) {
        const end = moment(this.appointment.endTime);
        this.endDate = end.toDate();
        this.endTime = end.format('HH:mm');
      }
      this.form.patchValue({
        notes: this.appointment.notes,
        employeeId: this.appointment.employeeId,
      });
      if ((this.appointment as any).contact?.id) {
        this.contactsService.getv2((this.appointment as any).contact.id, this.login.company.id).subscribe(fullContact => {
          this.contact = fullContact;
          this.form.patchValue({
            countryIso: fullContact.countryOfResidenceIso,
            manualMarketing: fullContact.manualMarketing,
          });
        });
      }
    }
  }

  syncAppointmentClicked() {
    this.isSyncingInBackground = true;
    this.externalCalendarSyncService.sync(this.appointment?.id!).pipe(delay(500)).subscribe(
      () => { toast(tr('Synchronized successfully')); this.isSyncingInBackground = false; },
      (e) => { toast(tr('An error occurred. Try later')); this.isSyncingInBackground = false; }
    );
  }

  cancelClicked() {
    this.closeClick.emit();
  }

  saveClicked() {
    if (this.form.invalid) return;
    if (!this.form.value.allDay && this.endDateTime?.isSameOrBefore(this.startDateTime)) {
      toast('End time cannot be before start time').then();
      return;
    }

    if (!this.appointment) this.createAppointment();
    else this.updateAppointment();
  }

  createAppointment() {
    delete this.form.value.client;
    delete this.form.value.clientSearch;
    const finalValue = {
      ...this.form.value,
      winery: { id: this.login.company.id },
      notes: this.form.value.notes,
      employee: { id: this.form.value.employee },
    };
    if (finalValue.allDay) finalValue.endTime = null;
    LoadingComponent.loading = true;
    this.appointmentsService.create(finalValue)
      .subscribe(() => {
        toast(`${tr('Appointment created.')}`).then();
        this.closeClick.emit();
      }, () => {
        toast(`${tr('There was a problem saving the appointment. Try again later.')}`).then();
      })
      .add(() => LoadingComponent.loading = false);
  }

  updateAppointment() {
    if (!this.appointment) return;
    const updatedValue = {
      ...this.form.value,
      winery: { id: this.login.company.id },
      notes: this.form.value.notes,
      employee: { id: this.form.value.employee },
    };
    if (updatedValue.allDay) updatedValue.endTime = null;
    LoadingComponent.loading = true;
    this.appointmentsService.update(this.appointment.id as string, updatedValue)
      .subscribe(() => {
        toast(`${tr('Appointment updated.')}`).then();
        this.closeClick.emit();
      }, () => {
        toast(`${tr('There was a problem updating the appointment. Try again later.')}`).then();
      })
      .add(() => LoadingComponent.loading = false);
  }

  patchContact(contact: any) {
    this.contact = contact;
    this.form.patchValue({
      firstname: contact.firstName,
      lastname: contact.lastName,
      email: contact.email,
      phone: contact.phoneNumber || contact.phone,
      countryIso: contact.countryIso || contact.countryOfResidenceIso,
      manualMarketing: contact.manualMarketing
    })
  }

  get hasClient(): boolean {
    const { firstname, lastname, email, phone } = this.form.value;
    return !!(firstname || lastname || email || phone);
  }

  get clientLabel(): string {
    const { firstname, lastname, email, phone } = this.form.value;
    return [firstname, lastname].filter(Boolean).join(' ').trim() || email || phone || '';
  }

  patchClient(client: any) {
    this.form.patchValue({
      ...client
    })
  }

  addNewClientClicked() {
    this.addClientRequested({ callback: (client: Client) => this.clientAdded(client), client: undefined });
    setTimeout(() => this.trigger.closePanel());
  }

  addClientRequested(data: { client?: Client, callback: (client: Client) => void }) {
    this.clientSaveCallback = data.callback;
    if (data.client) this.clientForm.patchValue(data.client)
    this.clientSideNav.open();
  }

  clientAdded(client: Client) {
    this.patchClient(client);
  }

  showClientList() {
    setTimeout(() => this.trigger.openPanel());
  }

  clientCloseClicked($event: any) {
    if ($event) {
      this.patchContact($event);
    }
    this.contacts = [];
    this.clientSideNav.close();
  }

  removeClientClicked() {
    this.contact = {};
    this.contacts = [];
    this.form.controls.clientSearch.reset();
    this.form.controls.lastname.reset();
    this.form.controls.firstname.reset();
    this.form.controls.email.reset();
    this.form.controls.phone.reset();
    this.form.controls.countryIso.reset();
    this.form.controls.manualMarketing.reset();
    this.searchControl.reset();
  }

  editClientClicked() {
    const v = this.form.value;
    this.contact = {
      ...this.contact,
      firstName: v.firstname,
      lastName: v.lastname,
      email: v.email,
      phoneNumber: v.phone,
      countryIso: v.countryIso,
      manualMarketing: v.manualMarketing,
    } as any;
    this.clientSideNav.open();
  }

  async deleteAppointmentClicked() {
    if (!this.appointment) return;
    const confirmed = await confirm.yesno(tr('Are you sure you want to remove this appointment?'));
    if (!confirmed) return;
    LoadingComponent.loading = true;
    this.appointmentsService.remove(this.appointment.id as string)
      .subscribe(() => {
        toast(`${tr('Appointment removed.')}`).then();
        this.closeClick.emit();
      }, () => {
        toast(`${tr('There was a problem removing the appointment. Try again later.')}`).then();
      })
      .add(() => LoadingComponent.loading = false);
  }

  contactName(contact: Contact): string {
    if (!contact) return '';
    return contact.firstName && contact.lastName ? `${contact.firstName} ${contact.lastName}` : contact.name || '';
  }
}
